import clsx from 'clsx';
import hotkeys from 'hotkeys-js';
import type { FunctionComponent, InputHTMLAttributes, Ref } from 'react';
import { Fragment, useEffect, useRef } from 'react';

import { IconSearch } from '../../core/icons';
import { Input } from '../../core/input/Input';
import { useKeyboardShortcutDisplay } from './use-keyboard-shortcut-display';

export const KeyboardHint: FunctionComponent<{ readonly children: React.ReactNode; readonly className?: string }> = ({
    children,
    className,
}) => (
    <span
        className={clsx('text-gray-700 text-xxs font-normal leading-[1rem] select-none whitespace-nowrap', className)}
    >
        {children}
    </span>
);

export const OutlinedInputWrapper: FunctionComponent<{
    readonly children: React.ReactNode;
    readonly className?: string;
}> = ({ children, className }) => (
    <div className={clsx('bg-gray-200 rounded-lg py-xxs px-s', className)}>{children}</div>
);

type BaseSearchInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
    readonly ref?: Ref<HTMLInputElement>;
    readonly rightElement?: React.ReactNode;
};

type SearchInputWithHotkeyProps = BaseSearchInputProps & {
    readonly hotkey: string;
    readonly hotkeyScope: string;
};

type SearchInputWithoutHotkeyProps = BaseSearchInputProps & {
    readonly hotkey?: undefined;
    readonly hotkeyScope?: undefined;
};

export type SearchInputProps = SearchInputWithHotkeyProps | SearchInputWithoutHotkeyProps;

const SearchInputBase: FunctionComponent<BaseSearchInputProps & { readonly inputRef?: Ref<HTMLInputElement> }> = ({
    inputRef,
    rightElement,
    ...props
}) => {
    return (
        <div className="flex items-center gap-m">
            <label className="flex items-center justify-center shrink-0 opacity-50" htmlFor="search-input">
                <IconSearch size={16} />
            </label>
            <Input
                ref={inputRef}
                className="!bg-transparent !border-none !text-gray-800 flex-1 !text-sm !leading-[1.25rem] !outline-none placeholder:!text-gray-600"
                id="search-input"
                type="text"
                {...props}
            />
            {rightElement}
        </div>
    );
};

const SearchInputWithHotkey: FunctionComponent<SearchInputWithHotkeyProps> = ({
    hotkey,
    hotkeyScope,
    rightElement,
    ...props
}) => {
    const keyboardShortcut = useKeyboardShortcutDisplay(hotkey);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        hotkeys.setScope(hotkeyScope);

        hotkeys(`cmd+${hotkey.toLowerCase()},ctrl+${hotkey.toLowerCase()}`, hotkeyScope, () => {
            searchInputRef.current?.focus();
        });

        return () => {
            hotkeys.unbind(`cmd+${hotkey.toLowerCase()},ctrl+${hotkey.toLowerCase()}`, hotkeyScope);
        };
    }, [hotkey, hotkeyScope]);

    const combinedRightElement = (
        <Fragment>
            {rightElement}
            <KeyboardHint>{keyboardShortcut}</KeyboardHint>
        </Fragment>
    );

    return <SearchInputBase {...props} inputRef={searchInputRef} rightElement={combinedRightElement} />;
};

export const SearchInput: FunctionComponent<SearchInputProps> = (props) => {
    if (props.hotkey !== undefined && props.hotkeyScope !== undefined) {
        return <SearchInputWithHotkey {...props} />;
    }

    return <SearchInputBase {...props} />;
};

SearchInput.displayName = 'SearchInput';
